import {
  HHChip,
  HHIconButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { FilterList } from '@mui/icons-material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import {
  BillStatusType,
  BILL_DATE_FORMAT,
} from '../bill-grid-components/bill-management-types';
import { defaultBillManagementGridFilterModel } from './bill-level/bill-management-grid';
import { BillManagementFiltersProps } from './filters-toolbar';

export interface QuickFiltersProps extends BillManagementFiltersProps {
  filterToggle: () => void;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
}

export const QuickFilters = ({
  filterToggle,
  setFilterParams,
  filterParams,
  setPaginationModel,
}: QuickFiltersProps): JSX.Element => {
  const setQuickBillStatusFilterParams = (
    quickFilterInput: BillStatusType,
  ): void => {
    setPaginationModel({ page: 0, pageSize: 100 });
    setFilterParams({
      ...filterParams,
      holdReasons: [],
      held: quickFilterInput.held,
      submitted: quickFilterInput.submitted,
      submissionStatusTypes: [],
    });
  };

  const setQuickDateFilterParams = (
    quickFilterInput: Record<string, string>,
  ): void => {
    setPaginationModel({ page: 0, pageSize: 100 });
    const { startDate } = filterParams;
    if (!dayjs(startDate).isSame(dayjs(quickFilterInput.startDate))) {
      setFilterParams({
        ...filterParams,
        startDate: quickFilterInput.startDate,
        endDate: quickFilterInput.endDate,
      });
    } else {
      setFilterParams({
        ...filterParams,
        startDate: null,
        endDate: null,
      });
    }
  };

  const billStatusQuickFilters = [
    {
      displayName: 'Held Bills',
      value: { held: true, submitted: false },
    },
    {
      displayName: 'Submitted Bills',
      value: { held: false, submitted: true },
    },
    {
      displayName: 'Pending Bills',
      value: { held: false, submitted: false },
    },
  ];

  const dateQuickFilters = [
    {
      displayName: 'DOS within 30 days',
      value: {
        startDate: dayjs().subtract(30, 'day').format(BILL_DATE_FORMAT),
        endDate: dayjs().format(BILL_DATE_FORMAT),
      },
    },
    {
      displayName: 'DOS within 90 days',
      value: {
        startDate: dayjs().subtract(90, 'day').format(BILL_DATE_FORMAT),
        endDate: dayjs().format(BILL_DATE_FORMAT),
      },
    },
    {
      displayName: 'DOS within 6 months',
      value: {
        startDate: dayjs().subtract(6, 'months').format(BILL_DATE_FORMAT),
        endDate: dayjs().format(BILL_DATE_FORMAT),
      },
    },
  ];

  const customFiltersApplied =
    filterParams.userIds.length > 0 ||
    filterParams.sourceIds.length > 0 ||
    filterParams.clientIds.length > 0 ||
    filterParams.insurerIds.length > 0 ||
    filterParams.billingTypes.length > 0 ||
    filterParams.partnershipIds.length > 0 ||
    (dateQuickFilters.every(
      dateQuickFilter =>
        dateQuickFilter.value.startDate !== filterParams.startDate,
    ) &&
      filterParams.startDate !==
        defaultBillManagementGridFilterModel.startDate) ||
    (dateQuickFilters.every(
      dateQuickFilter => dateQuickFilter.value.endDate !== filterParams.endDate,
    ) &&
      filterParams.endDate !== defaultBillManagementGridFilterModel.endDate);

  return (
    <HHStack direction="row" alignItems="center" spacing={2}>
      {billStatusQuickFilters.map(filter => (
        <HHChip
          key={filter.displayName}
          clickable
          label={filter.displayName}
          size="small"
          hhVariant={
            filterParams.held === filter.value.held &&
            filterParams.submitted === filter.value.submitted
              ? 'filled'
              : 'outlined'
          }
          color={
            filterParams.held === filter.value.held &&
            filterParams.submitted === filter.value.submitted
              ? 'neutral'
              : 'default'
          }
          onClick={(): void => setQuickBillStatusFilterParams(filter.value)}
        />
      ))}
      {dateQuickFilters.map(filter => (
        <HHChip
          clickable
          key={filter.displayName}
          label={filter.displayName}
          size="small"
          hhVariant={
            dayjs(filterParams?.startDate).isSame(
              dayjs(filter.value.startDate),
              'day',
            )
              ? 'filled'
              : 'outlined'
          }
          color={
            dayjs(filterParams?.startDate).isSame(
              dayjs(filter.value.startDate),
              'day',
            )
              ? 'neutral'
              : 'default'
          }
          onClick={(): void => setQuickDateFilterParams(filter.value)}
        />
      ))}
      {customFiltersApplied ? (
        <HHChip
          key="custom-filters"
          clickable
          label="Custom filter applied"
          size="small"
          hhVariant="filled"
          color="neutral"
          onClick={filterToggle}
        />
      ) : null}
      <HHIconButton
        hhVariant="variant-bypass"
        size="medium"
        color="default"
        aria-label="filterToggle"
        onClick={filterToggle}
      >
        <FilterList />
      </HHIconButton>
    </HHStack>
  );
};
